import { IBillingPlan } from '@/api/user/organization/billing/get-billing-plans-query'
import type { SubscriptionType } from 'api/dist/shared-types/shared-billing-types.js'
import { useCallback, useMemo } from 'react'
import { isUnlimited } from '@/features/subscription/constants'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'
import { useBillingUser } from '@/features/subscription/billing/billing-user-context'

export type IBillingFeature = NonNullable<IBillingPlan['monthlyVersion']>['features'][number]['feature']

/**
 * Pure function to check if a billing feature is accessible
 * @returns true if access granted, false if denied, undefined if loading
 */
export function getHasBillingFeatureAccess({
    feature,
    isSuperUser,
    billingPlans,
    subscriptionType,
}: {
    feature: IBillingFeature
    isSuperUser: boolean | undefined
    subscriptionType?: SubscriptionType
    billingPlans: { plans: IBillingPlan[] } | undefined
}): boolean | undefined {
    if (isSuperUser) return true

    if (!billingPlans) return undefined

    if (!subscriptionType) return false

    const rawBillingPlan = billingPlans.plans.find((plan) => plan.type === subscriptionType)
    const billingPlan = rawBillingPlan?.monthlyVersion

    if (!billingPlan) return false

    const matchedFeature = billingPlan.features.find((f) => f.feature === feature)
    if (!matchedFeature) return false

    // Boolean features (e.g. hasIntegrations) store true/false as limit
    // Numeric features store the limit value (0 = disabled, >0 or -1 = enabled)
    return typeof matchedFeature.limit === 'boolean' ? matchedFeature.limit : matchedFeature.limit !== 0
}

export function useBillingFeatureLimit(feature: IBillingFeature) {
    const isSuperUser = useIsSuperUser()
    const billingUser = useBillingUser()
    const billingAndUsage = billingUser?.billingAndUsage

    const usage = useMemo(() => {
        if (!billingAndUsage) return undefined

        switch (feature) {
            case 'brandsLimit':
                return billingAndUsage.usage?.brands

            case 'credits':
                return billingAndUsage.usage?.credits

            case 'usersLimit':
                return billingAndUsage.usage?.users
            case 'researcherLimit':
            case 'hasResearcherFeature':
                return billingAndUsage.usage?.researcher
        }
    }, [billingAndUsage, feature])

    const getWillLimitBeReached = useCallback(
        (futureUsage: number) => {
            // Super users bypass all limits
            if (isSuperUser) return false

            if (!usage?.limit) return

            if (isUnlimited(usage.limit)) return false

            return usage.limit < futureUsage
        },
        [usage, isSuperUser]
    )

    return {
        usage,
        getWillLimitBeReached,
    }
}
